'use client';

import React, { useEffect, useId, useRef, useState } from 'react';
import { cn } from '../lib/utils';
import { useCompositionDensity } from './composition-density';
import { Input } from './input';
import { DateEntryInput } from './date-entry-input';
import { parseIsoDateInput } from './date-entry-helpers';

const REVERSED_RANGE_MESSAGE = 'Choose a start date on or before the end date.';

export interface RegisterDateRangeValue {
  from: string;
  to: string;
}

type RegisterDateRangeControl =
  | {
      from: string;
      to: string;
      onChange: (range: RegisterDateRangeValue) => void;
      defaultFrom?: never;
      defaultTo?: never;
    }
  | {
      from?: never;
      to?: never;
      onChange?: never;
      defaultFrom?: string;
      defaultTo?: string;
    };

export type RegisterDateRangeProps = RegisterDateRangeControl & {
  fromLabel?: string;
  toLabel?: string;
  fromName?: string;
  toName?: string;
  className?: string;
  /** Native ISO date input by default; AU entry displays DD/MM/YYYY and emits ISO dates. */
  entry?: 'native' | 'au';
  /** Keep To at or after From by default. */
  constrainTo?: boolean;
  /** Called when a newly entered pair has From after To. */
  onInvalid?: (range: RegisterDateRangeValue) => void;
};

/** ISO calendar dates only; the caller owns tenant-timezone conversion. */
export function RegisterDateRange(props: RegisterDateRangeProps) {
  const {
    fromLabel = 'From',
    toLabel = 'To',
    fromName,
    toName,
    className,
    entry = 'native',
    constrainTo = true,
  } = props;
  const [local, setLocal] = useState<RegisterDateRangeValue>({
    from: props.defaultFrom ?? '',
    to: props.defaultTo ?? '',
  });
  const controlled = Boolean(props.onChange);
  const range = controlled ? { from: props.from ?? '', to: props.to ?? '' } : local;
  const invalid = Boolean(range.from && range.to && range.from > range.to);
  const fromId = useId();
  const toId = useId();
  const errorId = useId();
  const density = useCompositionDensity();
  const toInputRef = useRef<HTMLInputElement>(null);
  const reportedInvalidRef = useRef(invalid);

  // AU To must commit a reversed date so the range can report and display it.
  useEffect(() => {
    if (entry !== 'au' || !toInputRef.current) return;
    if (constrainTo && invalid) {
      toInputRef.current.setCustomValidity(REVERSED_RANGE_MESSAGE);
    } else if (!constrainTo || parseIsoDateInput(toInputRef.current.value) !== null) {
      toInputRef.current.setCustomValidity('');
    }
  }, [entry, constrainTo, invalid]);

  function update(next: RegisterDateRangeValue) {
    const nextInvalid = Boolean(next.from && next.to && next.from > next.to);
    if (nextInvalid && !reportedInvalidRef.current) props.onInvalid?.(next);
    if (next.to || entry !== 'au' || !toInputRef.current?.value) {
      reportedInvalidRef.current = nextInvalid;
    }
    if (props.onChange) props.onChange(next);
    else setLocal(next);
  }

  return (
    <div className={cn('min-w-0', className)}>
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <label htmlFor={fromId} className="shrink-0 text-caption text-text-muted">
            {fromLabel}
          </label>
          {entry === 'au' ? (
            <DateEntryInput
              id={fromId}
              name={fromName}
              size="toolbar"
              density={density}
              className="w-auto min-w-0"
              value={range.from}
              onChange={(value) => update({ from: value, to: range.to })}
              aria-invalid={invalid}
              aria-describedby={invalid ? errorId : undefined}
            />
          ) : (
            <Input
              id={fromId}
              type="date"
              name={fromName}
              size="toolbar"
              density={density}
              className="w-auto min-w-0"
              {...(controlled ? { value: range.from } : { defaultValue: props.defaultFrom })}
              onChange={(event) => update({ from: event.target.value, to: range.to })}
              aria-invalid={invalid}
              aria-describedby={invalid ? errorId : undefined}
            />
          )}
        </div>
        <div className="flex min-w-0 items-center gap-2">
          <label htmlFor={toId} className="shrink-0 text-caption text-text-muted">
            {toLabel}
          </label>
          {entry === 'au' ? (
            <DateEntryInput
              ref={toInputRef}
              id={toId}
              name={toName}
              size="toolbar"
              density={density}
              className="w-auto min-w-0"
              value={range.to}
              onChange={(value) => update({ from: range.from, to: value })}
              aria-invalid={invalid}
              aria-describedby={invalid ? errorId : undefined}
            />
          ) : (
            <Input
              id={toId}
              type="date"
              name={toName}
              size="toolbar"
              density={density}
              className="w-auto min-w-0"
              {...(controlled ? { value: range.to } : { defaultValue: props.defaultTo })}
              onChange={(event) => update({ from: range.from, to: event.target.value })}
              min={constrainTo ? range.from || undefined : undefined}
              aria-invalid={invalid}
              aria-describedby={invalid ? errorId : undefined}
            />
          )}
        </div>
      </div>
      {invalid && (
        <p id={errorId} role="alert" className="mt-1 text-caption text-status-danger-fg">
          {REVERSED_RANGE_MESSAGE}
        </p>
      )}
    </div>
  );
}
